<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Document } from '@tiptap/extension-document';
import { Text } from '@tiptap/extension-text';
import { Underline } from '@tiptap/extension-underline';
import { Highlight } from '@tiptap/extension-highlight';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { Heading } from '@tiptap/extension-heading';
import { Strike } from '@tiptap/extension-strike';
import { ListItem } from '@tiptap/extension-list-item';
import { BulletList } from '@tiptap/extension-bullet-list';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { TextAlign } from '@tiptap/extension-text-align';
import SpeechToText from './SpeechToText.vue';

const disabledClass = 'bg-gray-200 cursor-not-allowed';
const errorClass = 'border-red-500';
const validClass = 'border-indigo-600';

export default {
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    inherits: {
      type: Boolean,
      required: false,
      default: false,
    },
    error: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['update:value'],

  components: {
    SpeechToText,
    EditorContent,
  },

  data() {
    return {
      editor: null,
      tooltipLabelShown: false,
      valueBeforeSpeech: undefined,
      editorActions: [
        'bold',
        'italic',
        'underline',
        'strike',
        'highlight',
        'orderedList',
        'bulletList',
        'large',
        'small',
        'alignLeft',
        'alignCenter',
        'alignRight',
        'alignJustify',
      ],
    };
  },

  mounted() {
    this.editor = new Editor({
      editorProps: {
        attributes: {
          class: 'h-full min-h-36 p-6 overflow-auto resize-y',
        },
      },
      extensions: [
        Document,
        Paragraph,
        Text,
        Underline,
        Strike,
        Highlight,
        Bold,
        Italic,
        Heading,
        ListItem,
        OrderedList,
        BulletList,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ],
      content: this.value,
      onUpdate: () => {
        this.$emit('update:value', this.editor.getHTML());
      },
    });
  },

  beforeDestroy() {
    this.editor.destroy();
  },

  methods: {
    startRecognition () {
      this.valueBeforeSpeech = this.editor.getHTML();
    },

    stopRecognition() {
      this.valueBeforeSpeech = undefined;
    },

    updateMutable(value) {
      this.editor.commands.setContent(`${this.valueBeforeSpeech ?? ''} ${value}`, true);
    },

    handleAction(action) {
      switch (action) {
        case 'bold':
          this.editor.chain().focus().toggleBold().run();
          break;
        case 'italic':
          this.editor.chain().focus().toggleItalic().run();
          break;
        case 'underline':
          this.editor.chain().focus().toggleUnderline().run();
          break;
        case 'strike':
          this.editor.chain().focus().toggleStrike().run();
          break;
        case 'highlight':
          this.editor.chain().focus().toggleHighlight().run();
          break;
        case 'large':
          this.editor.chain().focus().toggleHeading({ level: 1 }).run();
          break;
        case 'small':
          this.editor.chain().focus().toggleHeading({ level: 3 }).run();
          break;
        case 'orderedList':
          this.editor.chain().focus().toggleOrderedList().run();
          break;
        case 'bulletList':
          this.editor.chain().focus().toggleBulletList().run();
          break;
        case 'alignLeft':
          console.log('align left');
          this.editor.chain().focus().setTextAlign('left').run();
          break;
        case 'alignCenter':
          this.editor.chain().focus().setTextAlign('center').run();
          break;
        case 'alignRight':
          this.editor.chain().focus().setTextAlign('right').run();
          break;
        case 'alignJustify':
          this.editor.chain().focus().setTextAlign('justify').run();
          break;
        default:
          break;
      }
    },
    actionIsActive(action) {
      switch (action) {
        case 'bold':
          return this.editor.isActive('bold');
        case 'italic':
          return this.editor.isActive('italic');
        case 'underline':
          return this.editor.isActive('underline');
        case 'strike':
          return this.editor.isActive('strike');
        case 'highlight':
          return this.editor.isActive('highlight');
        case 'large':
          return this.editor.isActive('heading', { level: 1 });
        case 'small':
          return this.editor.isActive('heading', { level: 3 });
        case 'orderedList':
          return this.editor.isActive('orderedList');
        case 'bulletList':
          return this.editor.isActive('bulletList');
        case 'alignLeft':
          return this.editor.isActive({ textAlign: 'left' });
        case 'alignCenter':
          return this.editor.isActive({ textAlign: 'center' });
        case 'alignRight':
          return this.editor.isActive({ textAlign: 'right' });
        case 'alignJustify':
          return this.editor.isActive({ textAlign: 'justify' });
        default:
          return false;
      }
    },

    actionName(action) {
      switch (action) {
        case 'bold':
          return 'gras';
        case 'italic':
          return 'italique';
        case 'underline':
          return 'souligné';
        case 'strike':
          return 'barré';
        case 'highlight':
          return 'surligné';
        case 'large':
          return 'grand';
        case 'small':
          return 'petit';
        case 'orderedList':
          return 'liste ordonnée';
        case 'bulletList':
          return 'liste à puces';
        case 'alignLeft':
          return 'aligner à gauche';
        case 'alignCenter':
          return 'centrer';
        case 'alignRight':
          return 'aligner à droite';
        case 'alignJustify':
          return 'justifier';
        default:
          return false;
      }
    },
  },

  computed: {
    editorClass() {
      if (this.disabled) {
        return disabledClass;
      } else if (this.error) {
        return errorClass;
      } else {
        return validClass;
      }
    },
  },

  watch: {
    value(value) {
      const isSame = this.editor.getHTML() === value;
      if (isSame) {
        return;
      }
      this.editor.commands.setContent(value, false);
    },
  },
};
</script>

<template>
  <div class="w-full col-span-full">
    <div class="flex flex-col relative">
      <p
        class="text-sm text-gray-600 font-normal truncate mb-2"
        :style="
          inherits
            ? 'color: inherit; font-size: inherit; font-weight: inherit;'
            : ''
        "
      >
        {{ label }}
      </p>
      <span v-if="required" class="text-pink-600 ml-1">*</span>
    </div>
    <div class="relative">
      <div
        id="editor-container"
        class="flex flex-row md:flex-col w-full bg-white border rounded-md border-opacity-50 focus:border-indigo-300 focus:outline-none focus:shadow-outline-indigo text-sm sm:leading-5 appearance-none placeholder-gray-600 transition duration-150 ease-in-out"
        :class="editorClass"
      >
        <div
          class="flex flex-col md:flex-row space-x-2 items-center justify-evenly bg-indigo-300 bg-opacity-50 p-2 border-b rounded-md border-indigo-600"
        >
          <button
            v-for="action in editorActions"
            :key="action"
            :name="action"
            class="min-h-10 bg-transparent rounded-md cursor-pointer hover:scale-110 transform transition-all duration-300 p-2 capitalize"
            :class="{
              'bg-indigo-600 text-gray-100': editor && actionIsActive(action),
            }"
            @click="handleAction(action)"
          >
            {{ actionName(action) }}
          </button>
        </div>
        <editor-content id="editor" class="flex-grow" :editor="editor" />
        <div
          class="absolute mb-4 right-0 inset-y-0 pt-10 pr-6 flex items-center cursor-pointer"
        >
          <SpeechToText
            @result="(result) => updateMutable(result)"
            @startRecognition="startRecognition"
            @stopRecognition="stopRecognition"
          ></SpeechToText>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
#editor :deep(ol) {
  list-style-type: decimal;
}
#editor :deep(ul) {
  list-style-type: disc;
}

/* #editor :deep(h1) {
  text-align: left;
  font-size: 16px;
}

#editor :deep(h3) {
  text-align: left;
  font-size: 12px;
} */

/* Font-size responsive */
@media (min-width: 1920px) {
  /* 1920x1080, 1080p@100% Full HD or more */
  #editor :deep(h1) {
    text-align: left;
    font-size: 21.3px;
  }
  #editor :deep(.text-base) {
    text-align: left;
    font-size: 17.3px;
  }

  #editor :deep(h3) {
    text-align: left;
    font-size: 13.3px;
  }
}

@media (min-width: 1440px) and (max-width: 1919px) {
  /* 1440x900, 1080p@125% wide screen */
  #editor :deep(h1) {
    text-align: left;
    font-size: 16px;
  }

  #editor :deep(.text-base) {
    text-align: left;
    font-size: 13px;
  }

  #editor :deep(h3) {
    text-align: left;
    font-size: 10px;
  }
}

@media (min-width: 1280px) and (max-width: 1439px) {
  /* 1280x720, 1366x768, 1080p@150% HD */
  #editor :deep(h1) {
    text-align: left;
    font-size: 14.2px;
  }

  #editor :deep(.text-base) {
    text-align: left;
    font-size: 11.5px;
  }

  #editor :deep(h3) {
    text-align: left;
    font-size: 8.8px;
  }
}

@media (min-width: 1024px) and (max-width: 1279px) {
  /* 1024x768 4/3 screen some tablets */
  #editor :deep(h1) {
    text-align: left;
    font-size: 14.2px;
  }

  #editor :deep(.text-base) {
    text-align: left;
    font-size: 11.5px;
  }

  #editor :deep(h3) {
    text-align: left;
    font-size: 8.8px;
  }
}

@media (max-width: 1023px) {
  /* other tablets or smartphones */
  #editor :deep(h1) {
    text-align: left;
    font-size: 14.2px;
  }

  #editor :deep(.text-base) {
    text-align: left;
    font-size: 11.5px;
  }

  #editor :deep(h3) {
    text-align: left;
    font-size: 8.8px;
  }
}
</style>
